/**
 * VersionTimelineSidebar
 *
 * Right sidebar showing version history with status badges and action buttons.
 * Supports create draft, deploy, rollback, delete, and diff actions.
 */

import React, { useState, useEffect } from 'react';
import { Rocket, Pencil, Trash2, RotateCcw, GitCompare } from 'lucide-react';
import { Button } from '../../../../ui/button';
import { ScrollArea } from '../../../../ui/scroll-area';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../../../ui/tooltip';
import VersionBadge from '../../shared/VersionBadge';
import ChangeSummaryBadges from '../../shared/ChangeSummaryBadges';
import DeployConfirmModal from '../../shared/DeployConfirmModal';
import MissingPromptsModal from '../../shared/MissingPromptsModal';
import RollbackModal from '../../shared/RollbackModal';
import DeleteConfirmModal from '../../shared/DeleteConfirmModal';
import CreateDraftModal from '../../shared/CreateDraftModal';
import DiffModal from './DiffModal';
import { highestVersion } from '../../utils/versionUtils';
import {
  useVersionList,
  useVersionDetail,
  useCreateDraft,
  useDeployVersion,
  useRollbackVersion,
  useDeleteDraft,
} from '../../hooks/useTaxonomy';
import { useLivePrompts } from '../../hooks/usePrompts';
import type { TaxonomyVersionSummary } from '../../types/taxonomy';

interface VersionTimelineSidebarProps {
  taxonomyKey: string;
  onEditDraft?: (versionId: string) => void;
  liveVersionId?: string;
  selectedVersionId?: string | null;
  onSelectVersion?: (versionId: string) => void;
  classifiedSignalCount?: number;
}

const VersionTimelineSidebar: React.FC<VersionTimelineSidebarProps> = ({
  taxonomyKey,
  onEditDraft,
  liveVersionId,
  selectedVersionId,
  onSelectVersion,
  classifiedSignalCount,
}) => {
  const { data: versions, isLoading } = useVersionList(taxonomyKey);
  const createDraft = useCreateDraft();
  const deployVersion = useDeployVersion();
  const rollbackVersion = useRollbackVersion();
  const deleteDraft = useDeleteDraft();

  const [deployModalVersion, setDeployModalVersion] = useState<TaxonomyVersionSummary | null>(null);
  const [rollbackModalVersion, setRollbackModalVersion] = useState<TaxonomyVersionSummary | null>(null);
  const [deleteModalVersion, setDeleteModalVersion] = useState<TaxonomyVersionSummary | null>(null);
  const [diffVersions, setDiffVersions] = useState<{ a: TaxonomyVersionSummary; b: TaxonomyVersionSummary } | null>(null);
  const [createDraftOpen, setCreateDraftOpen] = useState(false);

  // Deploy pre-validation: check all dimensions have live prompts.
  // Stores the full { id, versionNumber } so the effect can verify that
  // the loaded detail matches the pending version — preventing a race
  // when the user clicks deploy twice on different versions before the
  // first query returns.
  const { data: livePrompts, isError: livePromptsError } = useLivePrompts(taxonomyKey);
  const [pendingDeploy, setPendingDeploy] = useState<{
    versionId: string;
    versionNumber: string;
  } | null>(null);
  const { data: pendingVersionDetail, isError: versionDetailError } = useVersionDetail(
    taxonomyKey,
    pendingDeploy?.versionId || ''
  );
  const [missingPromptsData, setMissingPromptsData] = useState<{
    versionNumber: string;
    dimensions: Array<{ id: string; name: string }>;
  } | null>(null);

  useEffect(() => {
    if (!pendingDeploy) return;

    // If either query errored, skip the client-side pre-check and open
    // the deploy modal directly. The backend still has its own
    // prompt-completeness check, so the user will get a server error
    // instead of a silently dead button.
    if (livePromptsError || versionDetailError) {
      const v = versions?.find(v => v.id === pendingDeploy.versionId) ?? null;
      setDeployModalVersion(v);
      setPendingDeploy(null);
      return;
    }

    if (!pendingVersionDetail || livePrompts === undefined) return;
    // Guard against stale query data from a previous click.
    if (pendingVersionDetail.id !== pendingDeploy.versionId) return;

    const dimensions = pendingVersionDetail.taxonomyData?.dimensions ?? [];
    const missing = dimensions.filter(d => !(d.id in (livePrompts ?? {})));

    if (missing.length > 0) {
      setMissingPromptsData({
        versionNumber: pendingDeploy.versionNumber,
        dimensions: missing.map(d => ({ id: d.id, name: d.name })),
      });
    } else {
      const v = versions?.find(v => v.id === pendingDeploy.versionId) ?? null;
      setDeployModalVersion(v);
    }

    setPendingDeploy(null);
  }, [pendingVersionDetail, pendingDeploy, livePrompts, livePromptsError, versionDetailError, versions]);

  const hasDraft = versions?.some((v) => v.status === 'draft');
  const liveV = versions?.find((v) => v.status === 'live');

  // Pre-compute values for CreateDraftModal (avoids IIFE in JSX)
  const allVersionNums = versions?.map((v) => v.versionNumber).filter(Boolean) as string[] ?? [];
  const draftHighestVersion = highestVersion(
    allVersionNums.length > 0 ? allVersionNums : (liveV ? [liveV.versionNumber] : ['0.0'])
  );
  const draftLatestVersion = versions?.find((v) => v.versionNumber === draftHighestVersion);

  const handleCreateDraft = (versionNumber: string, sourceVersionId?: string) => {
    createDraft.mutate({ taxonomyKey, options: { versionNumber, sourceVersionId } }, {
      onSuccess: (data) => {
        setCreateDraftOpen(false);
        onSelectVersion?.(data.id);
      },
    });
  };

  const handleDeploy = (note: string) => {
    if (deployModalVersion) {
      deployVersion.mutate(
        { taxonomyKey, versionId: deployModalVersion.id, deploymentNote: note },
        { onSuccess: () => setDeployModalVersion(null) }
      );
    }
  };

  const handleRollback = (note: string) => {
    if (rollbackModalVersion) {
      rollbackVersion.mutate(
        { taxonomyKey, versionId: rollbackModalVersion.id, note },
        { onSuccess: () => setRollbackModalVersion(null) }
      );
    }
  };

  const handleDelete = () => {
    if (deleteModalVersion) {
      deleteDraft.mutate({ taxonomyKey, versionId: deleteModalVersion.id }, {
        onSuccess: () => {
          setDeleteModalVersion(null);
          if (liveVersionId) onSelectVersion?.(liveVersionId);
        },
      });
    }
  };

  if (isLoading) {
    return (
      <div className="w-[340px] flex-shrink-0">
        <div className="rounded-lg border bg-white p-4 space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-20 bg-gray-100 animate-pulse rounded" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="w-[340px] flex-shrink-0">
        <div className="rounded-lg border bg-white py-4">
          <div className="flex items-center justify-between mb-3 px-4">
            <h3 className="font-semibold text-sm">Version History</h3>
            {!hasDraft && versions && versions.length > 0 && (
              <Button
                variant="toolbar"
                size="sm"
                onClick={() => setCreateDraftOpen(true)}
                disabled={createDraft.isPending}
              >
                Create Draft
              </Button>
            )}
          </div>

          <ScrollArea className="h-[calc(100vh-320px)] px-4">
            <div className="space-y-2">
              {versions?.map((v) => (
                <div
                  key={v.id}
                  className={`border rounded-md p-3 cursor-pointer transition-colors flex flex-col gap-1.5 ${
                    selectedVersionId === v.id
                      ? 'border-blue-300 bg-blue-50'
                      : 'hover:bg-gray-50'
                  }`}
                  onClick={() => onSelectVersion?.(v.id)}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">
                      v{v.versionNumber}
                      {v.labelCount !== null && <span className="text-xs font-normal text-gray-400 ml-1.5">{v.labelCount} labels, {v.dimensionCount} dimensions</span>}
                    </span>
                    <VersionBadge status={v.status} />
                  </div>

                  <div className={v.changeSummary ? '' : 'invisible'}>
                    {v.changeSummary && <ChangeSummaryBadges summary={v.changeSummary} />}
                    {!v.changeSummary && <span className="text-xs">&nbsp;</span>}
                  </div>

                  <div className="flex items-center justify-between min-h-[28px]">
                    <p className="text-xs text-gray-500 truncate mr-1">
                      {new Date(v.createdAt).toLocaleDateString()}
                      {v.deployedAt && ` · Deployed ${new Date(v.deployedAt).toLocaleDateString()}`}
                    </p>

                    {/* Action icons */}
                    <TooltipProvider delayDuration={300}>
                      <div className="flex gap-0.5 flex-shrink-0">
                      {v.status === 'draft' && (
                        <>
                          {onEditDraft && (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button size="icon" variant="ghost" className="h-7 w-7" aria-label="Edit draft" onClick={(e) => { e.stopPropagation(); onEditDraft(v.id); }}>
                                  <Pencil className="h-3.5 w-3.5" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>Edit draft</TooltipContent>
                            </Tooltip>
                          )}
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button size="icon" variant="ghost" className="h-7 w-7" aria-label="Deploy to live" onClick={(e) => { e.stopPropagation(); setPendingDeploy({ versionId: v.id, versionNumber: v.versionNumber }); }}>
                                <Rocket className="h-3.5 w-3.5" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>Deploy to live</TooltipContent>
                          </Tooltip>
                          {liveVersionId && (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button size="icon" variant="ghost" className="h-7 w-7" aria-label="Diff vs live" onClick={(e) => {
                                  e.stopPropagation();
                                  const liveV = versions?.find((ver) => ver.id === liveVersionId);
                                  if (liveV) setDiffVersions({ a: liveV, b: v });
                                }}>
                                  <GitCompare className="h-3.5 w-3.5" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>Diff vs live</TooltipContent>
                            </Tooltip>
                          )}
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button size="icon" variant="ghost" className="h-7 w-7 text-red-600 hover:text-red-700" aria-label="Delete draft" onClick={(e) => { e.stopPropagation(); setDeleteModalVersion(v); }}>
                                <Trash2 className="h-3.5 w-3.5" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>Delete draft</TooltipContent>
                          </Tooltip>
                        </>
                      )}
                      {v.status === 'archived' && (
                        <>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button size="icon" variant="ghost" className="h-7 w-7" aria-label="Rollback to this version" onClick={(e) => { e.stopPropagation(); setRollbackModalVersion(v); }}>
                                <RotateCcw className="h-3.5 w-3.5" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>Rollback to this version</TooltipContent>
                          </Tooltip>
                          {liveVersionId && (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button size="icon" variant="ghost" className="h-7 w-7" aria-label="Diff vs live" onClick={(e) => {
                                  e.stopPropagation();
                                  const liveV = versions?.find((ver) => ver.id === liveVersionId);
                                  if (liveV) setDiffVersions({ a: v, b: liveV });
                                }}>
                                  <GitCompare className="h-3.5 w-3.5" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>Diff vs live</TooltipContent>
                            </Tooltip>
                          )}
                        </>
                      )}
                      </div>
                    </TooltipProvider>
                  </div>
                </div>
              ))}

              {(!versions || versions.length === 0) && (
                <p className="text-sm text-gray-500 text-center py-4">
                  No versions yet.
                </p>
              )}
            </div>
          </ScrollArea>
        </div>
      </div>

      {/* Modals */}
      <MissingPromptsModal
        open={!!missingPromptsData}
        onOpenChange={(open) => { if (!open) setMissingPromptsData(null); }}
        versionNumber={missingPromptsData?.versionNumber ?? ''}
        missingDimensions={missingPromptsData?.dimensions ?? []}
      />

      {deployModalVersion && (
        <DeployConfirmModal
          open={!!deployModalVersion}
          onOpenChange={(open) => !open && setDeployModalVersion(null)}
          versionNumber={deployModalVersion.versionNumber}
          type="taxonomy"
          onConfirm={handleDeploy}
          isDeploying={deployVersion.isPending}
          signalCount={classifiedSignalCount}
        />
      )}

      {rollbackModalVersion && (
        <RollbackModal
          open={!!rollbackModalVersion}
          onOpenChange={(open) => !open && setRollbackModalVersion(null)}
          versionNumber={rollbackModalVersion.versionNumber}
          type="taxonomy"
          onConfirm={handleRollback}
          isRollingBack={rollbackVersion.isPending}
        />
      )}

      {deleteModalVersion && (
        <DeleteConfirmModal
          open={!!deleteModalVersion}
          onOpenChange={(open) => !open && setDeleteModalVersion(null)}
          versionNumber={deleteModalVersion.versionNumber}
          type="taxonomy"
          onConfirm={handleDelete}
          isDeleting={deleteDraft.isPending}
        />
      )}

      {diffVersions && (
        <DiffModal
          taxonomyKey={taxonomyKey}
          open={!!diffVersions}
          onOpenChange={(open) => !open && setDiffVersions(null)}
          versionAId={diffVersions.a.id}
          versionBId={diffVersions.b.id}
          versionALabel={`v${diffVersions.a.versionNumber}`}
          versionBLabel={`v${diffVersions.b.versionNumber}`}
        />
      )}

      {createDraftOpen && (
        <CreateDraftModal
          open={createDraftOpen}
          onOpenChange={setCreateDraftOpen}
          currentVersion={draftHighestVersion}
          type="taxonomy"
          onConfirm={handleCreateDraft}
          isCreating={createDraft.isPending}
          liveSource={liveV ? { id: liveV.id, versionNumber: liveV.versionNumber } : undefined}
          latestSource={draftLatestVersion ? { id: draftLatestVersion.id, versionNumber: draftLatestVersion.versionNumber } : undefined}
        />
      )}
    </>
  );
};

export default VersionTimelineSidebar;
